import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";
import type { BusinessListingApplicationListItemResponse } from "../../types/businessListingTypes";
import { formatDateTime } from "../../utils/DateUtils";

type ApplicantStatusFilter = "ACTIVE" | "REJECTED";
type ResumeFilter = "ALL" | "HAS_RESUME";

type GenerateApplicantsPdfOptions = {
  listingTitle: string;
  applications: BusinessListingApplicationListItemResponse[];
  statusFilter: ApplicantStatusFilter;
  resumeFilter: ResumeFilter;
  search: string;
  includeResumes: boolean;
};

const pageWidth = 612;
const pageHeight = 792;
const margin = 48;
const contentWidth = pageWidth - (margin * 2);
const lineHeight = 15;

const applicationStatusLabel = {
  APPLIED: "Active",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawn",
} as const;

const filterLabel = {
  ACTIVE: "Active applicants",
  REJECTED: "Rejected applicants",
} as const satisfies Record<ApplicantStatusFilter, string>;

const resumeFilterLabel = {
  ALL: "All resumes",
  HAS_RESUME: "Has resume",
} as const satisfies Record<ResumeFilter, string>;

type PdfWriter = {
  doc: PDFDocument;
  font: PDFFont;
  boldFont: PDFFont;
  page: PDFPage;
  y: number;
};

const sanitizeFileName = (value: string) =>
  value
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "applicants";

const wrapText = (text: string, font: PDFFont, size: number, maxWidth: number) => {
  const lines: string[] = [];
  const paragraphs = text.split(/\r?\n/);

  paragraphs.forEach((paragraph) => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      lines.push("");
      return;
    }

    let currentLine = "";
    words.forEach((word) => {
      const nextLine = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(nextLine, size) <= maxWidth) {
        currentLine = nextLine;
        return;
      }
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    });
    if (currentLine) {
      lines.push(currentLine);
    }
  });

  return lines;
};

const addPage = (writer: PdfWriter) => {
  writer.page = writer.doc.addPage([pageWidth, pageHeight]);
  writer.y = pageHeight - margin;
};

const ensureSpace = (writer: PdfWriter, height: number) => {
  if (writer.y - height < margin) {
    addPage(writer);
  }
};

const drawText = (
  writer: PdfWriter,
  text: string,
  options?: {
    size?: number;
    bold?: boolean;
    color?: ReturnType<typeof rgb>;
    spacingAfter?: number;
    x?: number;
    maxWidth?: number;
  },
) => {
  const size = options?.size ?? 10;
  const font = options?.bold ? writer.boldFont : writer.font;
  const x = options?.x ?? margin;
  const maxWidth = options?.maxWidth ?? contentWidth;
  const lines = wrapText(text, font, size, maxWidth);
  const height = Math.max(lines.length, 1) * lineHeight + (options?.spacingAfter ?? 0);

  ensureSpace(writer, height);

  lines.forEach((line) => {
    writer.page.drawText(line, {
      x,
      y: writer.y,
      size,
      font,
      color: options?.color ?? rgb(0.08, 0.12, 0.2),
    });
    writer.y -= lineHeight;
  });
  writer.y -= options?.spacingAfter ?? 0;
};

const drawRule = (writer: PdfWriter, spacingAfter = 14) => {
  ensureSpace(writer, spacingAfter + 2);
  writer.page.drawLine({
    start: { x: margin, y: writer.y },
    end: { x: pageWidth - margin, y: writer.y },
    thickness: 1,
    color: rgb(0.84, 0.88, 0.94),
  });
  writer.y -= spacingAfter;
};

const drawField = (writer: PdfWriter, label: string, value: string) => {
  drawText(writer, label, { size: 9, bold: true, spacingAfter: 1 });
  drawText(writer, value, { size: 10, spacingAfter: 8 });
};

const getResumeFileName = (application: BusinessListingApplicationListItemResponse) =>
  application.resume?.originalFileName?.trim() || "Attached resume";

const drawApplicantSummary = (writer: PdfWriter, application: BusinessListingApplicationListItemResponse) => {
  ensureSpace(writer, 120);
  drawText(writer, application.teenDisplayName, { size: 16, bold: true, spacingAfter: 2 });
  drawText(
    writer,
    [
      applicationStatusLabel[application.status],
      `Applied ${formatDateTime(application.createdAt)}`,
      application.teenAge !== null ? `Age ${application.teenAge}` : null,
    ].filter(Boolean).join(" - "),
    { size: 10, color: rgb(0.25, 0.32, 0.42), spacingAfter: 10 },
  );

  drawField(
    writer,
    "Email",
    application.teenEmail?.trim() ? application.teenEmail : "No email provided.",
  );
  drawField(
    writer,
    "Resume",
    application.resume ? getResumeFileName(application) : "No resume attached.",
  );
  drawField(
    writer,
    "Profile description",
    application.teenBio?.trim() ? application.teenBio : "No profile description added.",
  );
  drawField(
    writer,
    "Skills",
    application.teenSkills.length > 0 ? application.teenSkills.join(", ") : "No skills added.",
  );
  drawField(
    writer,
    "Application note",
    application.message?.trim() ? application.message : "No note added with this application.",
  );
  if (application.rejectionReason?.trim()) {
    drawField(writer, "Rejection reason", application.rejectionReason);
  }
  drawRule(writer, 18);
};

const fetchResumeBytes = async (url: string) => {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new Error(`Resume download failed with ${response.status}`);
  }
  return response.arrayBuffer();
};

const addUnsupportedResumePage = (writer: PdfWriter, application: BusinessListingApplicationListItemResponse) => {
  addPage(writer);
  drawText(writer, `Resume for ${application.teenDisplayName}`, { size: 18, bold: true, spacingAfter: 12 });
  drawText(writer, getResumeFileName(application), { size: 12, bold: true, spacingAfter: 8 });
  drawText(
    writer,
    "This resume file type cannot be rendered into the PDF from the browser. Use the resume download link in the applicant details page to open the original file.",
    { size: 10, spacingAfter: 8 },
  );
};

const addResumeErrorPage = (writer: PdfWriter, application: BusinessListingApplicationListItemResponse) => {
  addPage(writer);
  drawText(writer, `Resume for ${application.teenDisplayName}`, { size: 18, bold: true, spacingAfter: 12 });
  drawText(writer, getResumeFileName(application), { size: 12, bold: true, spacingAfter: 8 });
  drawText(
    writer,
    "The resume could not be added to this PDF. Use the resume download link in the applicant details page to open the original file.",
    { size: 10, spacingAfter: 8 },
  );
};

const addImageResume = async (
  writer: PdfWriter,
  application: BusinessListingApplicationListItemResponse,
  bytes: ArrayBuffer,
) => {
  const contentType = application.resume?.contentType ?? "";
  const image = contentType.includes("png")
    ? await writer.doc.embedPng(bytes)
    : await writer.doc.embedJpg(bytes);
  addPage(writer);
  drawText(writer, `Resume for ${application.teenDisplayName}`, { size: 18, bold: true, spacingAfter: 12 });

  const maxImageWidth = contentWidth;
  const maxImageHeight = writer.y - margin;
  const scale = Math.min(maxImageWidth / image.width, maxImageHeight / image.height, 1);
  const width = image.width * scale;
  const height = image.height * scale;
  writer.page.drawImage(image, {
    x: margin + ((contentWidth - width) / 2),
    y: writer.y - height,
    width,
    height,
  });
};

const appendResume = async (writer: PdfWriter, application: BusinessListingApplicationListItemResponse) => {
  if (!application.resume) {
    return false;
  }

  const contentType = application.resume.contentType.toLowerCase();
  if (!contentType.includes("pdf") && !contentType.includes("png") && !contentType.includes("jpeg") && !contentType.includes("jpg")) {
    addUnsupportedResumePage(writer, application);
    return true;
  }

  try {
    const bytes = await fetchResumeBytes(application.resume.url);
    if (contentType.includes("pdf")) {
      const resumeDoc = await PDFDocument.load(bytes);
      const copiedPages = await writer.doc.copyPages(resumeDoc, resumeDoc.getPageIndices());
      copiedPages.forEach((page) => writer.doc.addPage(page));
      return true;
    }

    await addImageResume(writer, application, bytes);
    return true;
  } catch {
    addResumeErrorPage(writer, application);
    return true;
  }
};

export const generateApplicantsPdf = async ({
  listingTitle,
  applications,
  statusFilter,
  resumeFilter,
  search,
  includeResumes,
}: GenerateApplicantsPdfOptions) => {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const writer: PdfWriter = {
    doc,
    font,
    boldFont,
    page: doc.addPage([pageWidth, pageHeight]),
    y: pageHeight - margin,
  };

  drawText(writer, "Applicant Report", { size: 11, bold: true, color: rgb(0.29, 0.36, 0.46), spacingAfter: 4 });
  drawText(writer, listingTitle, { size: 24, bold: true, spacingAfter: 8 });
  drawText(
    writer,
    [
      filterLabel[statusFilter],
      resumeFilterLabel[resumeFilter],
      search.trim() ? `Search: ${search.trim()}` : null,
    ].filter(Boolean).join(" - "),
    { size: 10, color: rgb(0.25, 0.32, 0.42), spacingAfter: 2 },
  );
  drawText(
    writer,
    `${applications.length} applicant${applications.length === 1 ? "" : "s"} - Generated ${formatDateTime(new Date().toISOString())}`,
    { size: 10, color: rgb(0.25, 0.32, 0.42), spacingAfter: 14 },
  );
  drawRule(writer, 18);

  if (applications.length === 0) {
    drawText(writer, "No matching applicants.", { size: 11 });
  } else {
    for (const [index, application] of applications.entries()) {
      drawApplicantSummary(writer, application);
      if (includeResumes) {
        const appendedResumePages = await appendResume(writer, application);
        if (appendedResumePages && index < applications.length - 1) {
          addPage(writer);
        }
      }
    }
  }

  const bytes = await doc.save();
  return {
    bytes,
    fileName: `${sanitizeFileName(listingTitle)}-applicants.pdf`,
  };
};
